import { useEffect, useMemo, useState, type FormEvent } from 'react';
import {
  Surface,
  ghostButtonStyle,
  ghostLinkStyle,
  inputStyle,
  primaryButtonStyle,
} from '@kilat/ui';
import { Link } from 'react-router-dom';

import { useAuth } from '../lib/use-auth.js';
import { useI18n, type I18n } from '../lib/use-i18n.js';
import { useTrackedSnapshot } from '../lib/use-tracked-snapshot.js';
import { useActiveOutlet } from '../lib/use-active-outlet.js';
import { outletDocRepoFor } from '../lib/repositories/outlet-repos.js';
import { subscriptionRepoFor } from '../lib/repositories/subscription-repo.js';
import { subscriptionEventsRepoFor } from '../lib/repositories/subscription-events-repo.js';
import { logger } from '../lib/logger.js';
import {
  callAddOutlet,
  callCancelOutletRemoval,
  callRemoveOutlet,
  type AddOutletOutput,
} from '../lib/callables/billing.js';
import { defaultPlanId, defaultPlans } from '@kilat/config-seed';
import { money, proration, type SubscriptionEvent, type SubscriptionState } from '@kilat/shared';

/**
 * /outlets — operator manages locations.
 *
 * Left pane: live list of outlets with status pill + "Switch to" link.
 * Right pane: Subscription summary + "Add an outlet" form with a live
 * proration preview the operator sees BEFORE they hit Add.
 *
 * Premium-logical: the proration math runs in the browser using the same
 * shared helper the Cloud Function uses, so the displayed amount is
 * deterministic and matches what the addOutlet callable returns. The CTA
 * itself is gated behind a confirmation step so the operator never gets
 * surprise-charged.
 */

interface OutletRow {
  readonly id: string;
  readonly name: string;
  readonly slug: string;
  readonly status: string;
  readonly address: string;
}

export function OutletsPage() {
  const { claims } = useAuth();
  const t = useI18n();
  const tenantId =
    typeof claims?.['defaultTenant'] === 'string' ? (claims['defaultTenant'] as string) : null;

  const subscribe = useMemo(() => {
    if (!tenantId) return null;
    const repo = outletDocRepoFor(tenantId);
    return (cb: (vs: unknown[]) => void) => repo.listSnapshot(cb);
  }, [tenantId]);

  const { data: outletDocs, loading: outletsLoading } = useTrackedSnapshot<unknown[]>(
    ['outlets', tenantId],
    subscribe,
  );

  const eventsSubscribe = useMemo(() => {
    if (!tenantId) return null;
    const repo = subscriptionEventsRepoFor(tenantId);
    return (cb: (vs: SubscriptionEvent[]) => void) => repo.listSnapshot(cb);
  }, [tenantId]);
  const { data: subscriptionEvents } = useTrackedSnapshot<SubscriptionEvent[]>(
    ['subscriptionEvents', tenantId],
    eventsSubscribe,
  );

  const [subscription, setSubscription] = useState<SubscriptionState | null>(null);
  const [subLoaded, setSubLoaded] = useState(false);

  useEffect(() => {
    if (!tenantId) return;
    let cancelled = false;
    (async () => {
      try {
        const sub = await subscriptionRepoFor(tenantId).get('current');
        if (cancelled) return;
        setSubscription(sub);
      } catch (e) {
        logger.error('outlets: subscription load failed', { tenantId, error: String(e) });
      } finally {
        if (!cancelled) setSubLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tenantId]);

  // Map outletId → most-recent unmatched 'outlet_removal_scheduled' event.
  // An event is "unmatched" if no later 'outlet_removal_cancelled' references
  // it. We use this to drive the row-level "Closing on … — Undo" affordance.
  const pendingRemovals = useMemo(
    () => indexPendingRemovals(subscriptionEvents ?? []),
    [subscriptionEvents],
  );

  if (!tenantId) return <Screen body={t.t('dashboard.outlets.signInPrompt')} />;
  if (outletsLoading || !subLoaded) return <Screen body={t.t('common.loading')} />;

  const outlets: OutletRow[] = (outletDocs ?? []).map((raw) => {
    if (typeof raw !== 'object' || raw === null) {
      return { id: '—', name: '—', slug: '—', status: 'active', address: '' };
    }
    const r = raw as {
      id?: string;
      profile?: { name?: string; slug?: string; status?: string; address?: string };
    };
    return {
      id: r.id ?? '—',
      name: r.profile?.name ?? r.id ?? '—',
      slug: r.profile?.slug ?? r.id ?? '—',
      status: r.profile?.status ?? 'active',
      address: r.profile?.address ?? '',
    };
  });

  const activeOutletCount = outlets.filter((o) => o.status === 'active').length;

  return (
    <main
      className="min-h-dvh"
      style={{
        background: 'var(--tint-subtle)',
        color: 'var(--color-on-surface)',
        padding: 'var(--space-6) var(--space-5)',
      }}
    >
      <header
        style={{
          marginBottom: 'var(--space-6)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-end',
          gap: 'var(--space-4)',
        }}
      >
        <div>
          <h1 style={{ fontSize: 'var(--text-h1)', fontWeight: 600, margin: 0 }}>
            {t.t('dashboard.outlets.title')}
          </h1>
          <p
            style={{
              fontSize: 'var(--text-meta)',
              color: 'var(--color-on-surface-muted)',
              margin: 0,
            }}
          >
            {t.t(
              outlets.length === 1
                ? 'dashboard.outlets.locationCountOne'
                : 'dashboard.outlets.locationCountOther',
              { count: outlets.length },
            )}
          </p>
        </div>
        <Link to="/queue" style={ghostLinkStyle}>
          {t.t('dashboard.states.backToQueue')}
        </Link>
      </header>

      <div className="layout-split-aside">
        <Surface as="section">
          <h2 style={{ fontSize: 'var(--text-body)', fontWeight: 600, margin: 0 }}>
            {t.t('dashboard.outlets.yourLocations')}
          </h2>
          <div style={{ marginTop: 'var(--space-3)', display: 'grid', gap: 'var(--space-2)' }}>
            {outlets.map((o) => (
              <OutletRowView
                key={o.id}
                tenantId={tenantId}
                outlet={o}
                activeOutletCount={activeOutletCount}
                pendingRemoval={pendingRemovals.get(o.id) ?? null}
              />
            ))}
            {outlets.length === 0 ? (
              <p
                style={{
                  fontSize: 'var(--text-meta)',
                  color: 'var(--color-on-surface-muted)',
                  textAlign: 'center',
                  margin: `var(--space-6) 0`,
                }}
              >
                {t.t('dashboard.outlets.emptyNoOutlets')}
              </p>
            ) : null}
          </div>
        </Surface>

        <div style={{ display: 'grid', gap: 'var(--space-4)' }}>
          <Surface as="section">
            <SubscriptionSummary subscription={subscription} outletCount={outlets.length} />
          </Surface>

          <Surface as="section">
            <AddOutletForm tenantId={tenantId} />
          </Surface>
        </div>
      </div>
    </main>
  );
}

interface PendingRemoval {
  readonly eventId: string;
  readonly effectiveAtMs: number;
}

function OutletRowView({
  tenantId,
  outlet,
  activeOutletCount,
  pendingRemoval,
}: {
  readonly tenantId: string;
  readonly outlet: OutletRow;
  readonly activeOutletCount: number;
  readonly pendingRemoval: PendingRemoval | null;
}) {
  const t = useI18n();
  const { outletId: activeId, setOutletId } = useActiveOutlet();
  const isActive = outlet.id === activeId;
  const pill = pillForStatus(t, outlet.status);

  // Three sub-states for the close affordance, each with explicit next-CTA:
  //   idle   → "Close at end of month" (only when status='active' AND > 1 active)
  //   confirm → inline confirmation card with effectiveAt + "Close" / "Cancel"
  //   busy   → button disabled, label "Closing…" / "Undoing…"
  const [closeStage, setCloseStage] = useState<'idle' | 'confirm' | 'busy'>('idle');
  const [closeError, setCloseError] = useState<string | null>(null);

  const canCloseHere = outlet.status === 'active' && activeOutletCount > 1;
  const isPendingClose = outlet.status === 'soft_closed' && pendingRemoval !== null;

  async function doClose() {
    setCloseStage('busy');
    setCloseError(null);
    try {
      await callRemoveOutlet({ tenantId, outletId: outlet.id });
      setCloseStage('idle');
    } catch (e) {
      logger.error('removeOutlet call failed', { outletId: outlet.id, error: String(e) });
      setCloseError(extractFriendlyError(t, e));
      setCloseStage('confirm');
    }
  }

  async function doUndo() {
    if (!pendingRemoval) return;
    setCloseStage('busy');
    setCloseError(null);
    try {
      await callCancelOutletRemoval({
        tenantId,
        outletId: outlet.id,
        cancelledEventId: pendingRemoval.eventId,
      });
      setCloseStage('idle');
    } catch (e) {
      logger.error('cancelOutletRemoval call failed', { outletId: outlet.id, error: String(e) });
      setCloseError(extractFriendlyError(t, e));
      setCloseStage('idle');
    }
  }

  return (
    <div
      style={{
        display: 'grid',
        gap: 'var(--space-3)',
        padding: 'var(--space-3)',
        borderRadius: 'var(--radius-md)',
        border: '1px solid var(--color-border)',
        background: 'var(--color-surface)',
      }}
    >
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1fr) auto',
          gap: 'var(--space-3)',
        }}
      >
        <div style={{ minWidth: 0 }}>
          <p style={{ margin: 0, fontWeight: 500 }}>{outlet.name}</p>
          <p
            style={{
              margin: 'var(--space-1) 0 0 0',
              fontSize: 'var(--text-meta)',
              color: 'var(--color-on-surface-muted)',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
            title={outlet.address || outlet.slug}
          >
            /{outlet.slug}
            {outlet.address ? ` · ${outlet.address}` : ''}
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
          <span
            style={{
              background: pill.bg,
              color: pill.fg,
              padding: '4px 10px',
              borderRadius: 'var(--radius-pill)',
              fontSize: 'var(--text-meta)',
              fontWeight: 500,
            }}
          >
            {pill.label}
          </span>
          {isActive ? (
            <span
              style={{
                fontSize: 'var(--text-meta)',
                color: 'var(--color-on-surface-muted)',
                padding: '4px 10px',
              }}
            >
              {t.t('dashboard.outlets.selected')}
            </span>
          ) : outlet.status === 'active' ? (
            <button
              type="button"
              onClick={() => setOutletId(outlet.id)}
              style={miniGhostButtonStyle}
            >
              {t.t('dashboard.outlets.switchTo')}
            </button>
          ) : null}
        </div>
      </div>

      {/* Bottom row: close / pending-close / explanation for why close is gated */}
      {isPendingClose && pendingRemoval ? (
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 'var(--space-3)',
            padding: 'var(--space-3)',
            background: 'rgba(245,158,11,0.08)',
            border: '1px solid rgba(245,158,11,0.24)',
            borderRadius: 'var(--radius-sm)',
            fontSize: 'var(--text-meta)',
          }}
        >
          <span style={{ color: 'var(--color-on-surface-muted)' }}>
            {t.t('dashboard.outlets.closingOn', { date: formatDate(pendingRemoval.effectiveAtMs) })}
          </span>
          <button
            type="button"
            onClick={() => void doUndo()}
            disabled={closeStage === 'busy'}
            style={{ ...miniGhostButtonStyle, opacity: closeStage === 'busy' ? 0.5 : 1 }}
          >
            {closeStage === 'busy'
              ? t.t('dashboard.outlets.undoing')
              : t.t('dashboard.outlets.undo')}
          </button>
        </div>
      ) : canCloseHere && closeStage === 'idle' ? (
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button
            type="button"
            onClick={() => {
              setCloseError(null);
              setCloseStage('confirm');
            }}
            style={miniGhostButtonStyle}
          >
            {t.t('dashboard.outlets.closeAtEndOfMonth')}
          </button>
        </div>
      ) : canCloseHere && closeStage !== 'idle' ? (
        <CloseConfirmation
          outletName={outlet.name}
          busy={closeStage === 'busy'}
          error={closeError}
          onCancel={() => {
            setCloseError(null);
            setCloseStage('idle');
          }}
          onConfirm={() => void doClose()}
        />
      ) : outlet.status === 'active' && activeOutletCount <= 1 ? (
        <p
          style={{
            margin: 0,
            fontSize: 'var(--text-meta)',
            color: 'var(--color-on-surface-muted)',
            textAlign: 'right',
          }}
        >
          {t.t('dashboard.outlets.lastActiveOutlet')}
        </p>
      ) : null}
    </div>
  );
}

function CloseConfirmation({
  outletName,
  busy,
  error,
  onCancel,
  onConfirm,
}: {
  readonly outletName: string;
  readonly busy: boolean;
  readonly error: string | null;
  readonly onCancel: () => void;
  readonly onConfirm: () => void;
}) {
  const t = useI18n();
  const previewEffective = endOfMonthLocalDate();
  return (
    <div
      style={{
        display: 'grid',
        gap: 'var(--space-2)',
        padding: 'var(--space-3) var(--space-4)',
        background: 'rgba(239,68,68,0.04)',
        border: '1px solid rgba(239,68,68,0.18)',
        borderRadius: 'var(--radius-sm)',
      }}
    >
      <p style={{ margin: 0, fontWeight: 500 }}>
        {t.t('dashboard.outlets.confirmCloseTitle', { name: outletName })}
      </p>
      <p
        style={{ margin: 0, fontSize: 'var(--text-meta)', color: 'var(--color-on-surface-muted)' }}
      >
        {t.t('dashboard.outlets.confirmEffectivePre')}{' '}
        <strong>{formatDate(previewEffective)}</strong>
        {t.t('dashboard.outlets.confirmEffectivePost')}
      </p>
      {error ? (
        <p
          role="alert"
          style={{ margin: 0, color: 'var(--color-accent)', fontSize: 'var(--text-meta)' }}
        >
          {error}
        </p>
      ) : null}
      <div style={{ display: 'flex', gap: 'var(--space-2)', justifyContent: 'flex-end' }}>
        <button type="button" onClick={onCancel} disabled={busy} style={miniGhostButtonStyle}>
          {t.t('dashboard.outlets.keepOpen')}
        </button>
        <button
          type="button"
          onClick={onConfirm}
          disabled={busy}
          style={{
            ...miniGhostButtonStyle,
            background: 'var(--color-accent)',
            color: 'white',
            border: 'none',
            opacity: busy ? 0.6 : 1,
          }}
        >
          {busy ? t.t('dashboard.outlets.closing') : t.t('dashboard.outlets.closeAtEndOfMonth')}
        </button>
      </div>
    </div>
  );
}

function SubscriptionSummary({
  subscription,
  outletCount,
}: {
  readonly subscription: SubscriptionState | null;
  readonly outletCount: number;
}) {
  const t = useI18n();
  const plan = defaultPlans.find((p) => p.id === defaultPlanId);
  if (!plan) return null;
  const extras = Math.max(0, outletCount - plan.includedOutlets);
  const monthlyTotal = plan.basePrice + extras * plan.extraOutletPrice;
  const status = subscription?.status ?? 'trial';

  return (
    <div style={{ display: 'grid', gap: 'var(--space-3)' }}>
      <h2 style={{ fontSize: 'var(--text-body)', fontWeight: 600, margin: 0 }}>
        {t.t('dashboard.outlets.yourPlan')}
      </h2>
      <div style={{ display: 'grid', gap: 'var(--space-1)' }}>
        <Row
          label={t.t('dashboard.outlets.planLabel')}
          value={t.t('dashboard.outlets.planValue', {
            name: plan.name,
            price: money.format(plan.basePrice),
            included: plan.includedOutlets,
          })}
        />
        <Row
          label={t.t('dashboard.outlets.extraOutletsLabel')}
          value={
            extras === 0
              ? '—'
              : t.t('dashboard.outlets.extraOutletsValue', {
                  count: extras,
                  price: money.format(plan.extraOutletPrice),
                })
          }
        />
        <Row label={t.t('dashboard.outlets.statusFieldLabel')} value={statusLabel(t, status)} />
        <Row label={t.t('dashboard.outlets.outletsActiveLabel')} value={String(outletCount)} />
      </div>
      <hr style={{ border: 'none', borderTop: '1px solid var(--color-border)', margin: 0 }} />
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
        <span style={{ fontSize: 'var(--text-meta)', color: 'var(--color-on-surface-muted)' }}>
          {t.t('dashboard.outlets.monthlyTotalFull')}
        </span>
        <strong style={{ fontVariantNumeric: 'tabular-nums' }}>{money.format(monthlyTotal)}</strong>
      </div>
    </div>
  );
}

function AddOutletForm({ tenantId }: { readonly tenantId: string }) {
  const t = useI18n();
  const { setOutletId } = useActiveOutlet();
  const [name, setName] = useState('');
  const [address, setAddress] = useState('');
  const [stage, setStage] = useState<'form' | 'confirm' | 'success'>('form');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<AddOutletOutput | null>(null);

  // Live proration preview — computed client-side from the same shared helper
  // the Cloud Function uses. Recomputed on every render; effectively free.
  const plan = defaultPlans.find((p) => p.id === defaultPlanId);
  if (!plan) return null;
  const now = new Date();
  const previewAmount = proration.prorateAt(plan.extraOutletPrice, now);
  const day = now.getDate();
  const daysInMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
  const daysRemaining = daysInMonth - day + 1;

  function reset() {
    setName('');
    setAddress('');
    setError(null);
    setResult(null);
    setStage('form');
  }

  function goToConfirm(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    if (!name.trim()) {
      setError(t.t('dashboard.outlets.errNoName'));
      return;
    }
    setStage('confirm');
  }

  async function confirmAdd() {
    setSubmitting(true);
    setError(null);
    try {
      const out = await callAddOutlet({
        tenantId,
        name: name.trim(),
        address: address.trim(),
      });
      setResult(out);
      setStage('success');
    } catch (e) {
      logger.error('addOutlet call failed', { error: String(e) });
      setError(extractFriendlyError(t, e));
    } finally {
      setSubmitting(false);
    }
  }

  if (stage === 'success' && result) {
    return (
      <div style={{ display: 'grid', gap: 'var(--space-4)' }}>
        <header>
          <h2 style={{ fontSize: 'var(--text-body)', fontWeight: 600, margin: 0 }}>
            {t.t('dashboard.outlets.outletAddedTitle')}
          </h2>
          <p
            style={{
              fontSize: 'var(--text-meta)',
              color: 'var(--color-on-surface-muted)',
              margin: 'var(--space-1) 0 0 0',
            }}
          >
            {t.t('dashboard.outlets.outletAddedBody', {
              amount: money.format(result.prorationAmount),
              currency: result.currency,
              day: result.day,
              daysInMonth: result.daysInMonth,
              fullPrice: money.format(plan.extraOutletPrice),
            })}
          </p>
        </header>
        <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
          <button
            type="button"
            onClick={() => {
              setOutletId(result.outletId);
              reset();
            }}
            style={primaryButtonStyle}
          >
            {t.t('dashboard.outlets.switchToIt')}
          </button>
          <button type="button" onClick={reset} style={ghostButtonStyle}>
            {t.t('dashboard.outlets.addAnother')}
          </button>
        </div>
      </div>
    );
  }

  if (stage === 'confirm') {
    return (
      <div style={{ display: 'grid', gap: 'var(--space-4)' }}>
        <header>
          <h2 style={{ fontSize: 'var(--text-body)', fontWeight: 600, margin: 0 }}>
            {t.t('dashboard.outlets.confirmNewOutlet')}
          </h2>
          <p
            style={{
              fontSize: 'var(--text-meta)',
              color: 'var(--color-on-surface-muted)',
              margin: 'var(--space-1) 0 0 0',
            }}
          >
            {t.t(
              daysRemaining === 1
                ? 'dashboard.outlets.confirmProrationOne'
                : 'dashboard.outlets.confirmProrationOther',
              { days: daysRemaining },
            )}
          </p>
        </header>

        <ProrationBreakdown
          name={name.trim()}
          day={day}
          daysInMonth={daysInMonth}
          previewAmount={previewAmount}
          extraOutletPrice={plan.extraOutletPrice}
        />

        {error ? (
          <p
            role="alert"
            style={{ color: 'var(--color-accent)', fontSize: 'var(--text-meta)', margin: 0 }}
          >
            {error}
          </p>
        ) : null}

        <div style={{ display: 'flex', gap: 'var(--space-2)', justifyContent: 'space-between' }}>
          <button
            type="button"
            onClick={() => setStage('form')}
            disabled={submitting}
            style={ghostButtonStyle}
          >
            {t.t('common.back')}
          </button>
          <button
            type="button"
            onClick={() => void confirmAdd()}
            disabled={submitting}
            style={{ ...primaryButtonStyle, opacity: submitting ? 0.6 : 1 }}
          >
            {submitting
              ? t.t('dashboard.outlets.adding')
              : t.t('dashboard.outlets.addForToday', { amount: money.format(previewAmount) })}
          </button>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={goToConfirm} style={{ display: 'grid', gap: 'var(--space-4)' }}>
      <header>
        <h2 style={{ fontSize: 'var(--text-body)', fontWeight: 600, margin: 0 }}>
          {t.t('dashboard.outlets.addAnOutlet')}
        </h2>
        <p
          style={{
            fontSize: 'var(--text-meta)',
            color: 'var(--color-on-surface-muted)',
            margin: 'var(--space-1) 0 0 0',
          }}
        >
          {t.t('dashboard.outlets.seedNote')}
        </p>
      </header>

      <label style={fieldGrid}>
        <span style={fieldLabel}>{t.t('dashboard.outlets.nameLabel')}</span>
        <input
          required
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={t.t('dashboard.outlets.namePlaceholder')}
          autoComplete="organization"
          style={inputStyle}
        />
      </label>

      <label style={fieldGrid}>
        <span style={fieldLabel}>{t.t('dashboard.outlets.addressLabel')}</span>
        <textarea
          value={address}
          onChange={(e) => setAddress(e.target.value)}
          rows={2}
          placeholder={t.t('dashboard.outlets.addressPlaceholder')}
          style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }}
        />
      </label>

      <div
        style={{
          padding: 'var(--space-3) var(--space-4)',
          borderRadius: 'var(--radius-md)',
          background: 'rgba(13,122,79,0.06)',
          border: '1px solid rgba(13,122,79,0.18)',
        }}
      >
        <p
          style={{
            margin: 0,
            fontSize: 'var(--text-meta)',
            color: 'var(--color-on-surface-muted)',
          }}
        >
          {t.t('dashboard.outlets.todaysProratedCharge')}
        </p>
        <p
          style={{
            margin: 'var(--space-1) 0 0 0',
            fontSize: 'var(--text-h2)',
            fontWeight: 600,
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {money.format(previewAmount)}
        </p>
        <p
          style={{
            margin: 'var(--space-1) 0 0 0',
            fontSize: 'var(--text-meta)',
            color: 'var(--color-on-surface-muted)',
          }}
        >
          {t.t('dashboard.outlets.proratedFraction', {
            daysRemaining,
            daysInMonth,
            price: money.format(plan.extraOutletPrice),
          })}
        </p>
      </div>

      {error ? (
        <p
          role="alert"
          style={{ color: 'var(--color-accent)', fontSize: 'var(--text-meta)', margin: 0 }}
        >
          {error}
        </p>
      ) : null}

      <button type="submit" style={primaryButtonStyle}>
        {t.t('dashboard.outlets.review')}
      </button>
    </form>
  );
}

function ProrationBreakdown({
  name,
  day,
  daysInMonth,
  previewAmount,
  extraOutletPrice,
}: {
  readonly name: string;
  readonly day: number;
  readonly daysInMonth: number;
  readonly previewAmount: number;
  readonly extraOutletPrice: number;
}) {
  const t = useI18n();
  return (
    <div
      style={{
        display: 'grid',
        gap: 'var(--space-1)',
        padding: 'var(--space-4)',
        borderRadius: 'var(--radius-md)',
        background: 'var(--tint-subtle)',
      }}
    >
      <Row label={t.t('dashboard.outlets.outletFieldLabel')} value={name || '—'} />
      <Row
        label={t.t('dashboard.outlets.planExtraRateLabel')}
        value={t.t('dashboard.outlets.rateMo', { price: money.format(extraOutletPrice) })}
      />
      <Row
        label={t.t('dashboard.outlets.todayLabel')}
        value={t.t('dashboard.outlets.dayOfMonth', { day, total: daysInMonth })}
      />
      <hr
        style={{
          border: 'none',
          borderTop: '1px solid var(--color-border)',
          margin: 'var(--space-2) 0',
        }}
      />
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
        <span style={{ fontSize: 'var(--text-meta)', color: 'var(--color-on-surface-muted)' }}>
          {t.t('dashboard.outlets.chargedToday')}
        </span>
        <strong style={{ fontVariantNumeric: 'tabular-nums' }}>
          {money.format(previewAmount)}
        </strong>
      </div>
    </div>
  );
}

function Row({ label, value }: { readonly label: string; readonly value: string }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 'var(--space-3)' }}>
      <span style={{ fontSize: 'var(--text-meta)', color: 'var(--color-on-surface-muted)' }}>
        {label}
      </span>
      <span style={{ fontSize: 'var(--text-meta)', fontVariantNumeric: 'tabular-nums' }}>
        {value}
      </span>
    </div>
  );
}

function pillForStatus(t: I18n, status: string): { bg: string; fg: string; label: string } {
  switch (status) {
    case 'active':
      return {
        bg: 'rgba(13,122,79,0.10)',
        fg: 'var(--color-primary)',
        label: t.t('dashboard.outlets.statusActive'),
      };
    case 'soft_closed':
      return {
        bg: 'var(--color-border)',
        fg: 'var(--color-on-surface-muted)',
        label: t.t('dashboard.outlets.statusClosed'),
      };
    case 'archived':
      return {
        bg: 'rgba(239,68,68,0.10)',
        fg: 'var(--color-accent)',
        label: t.t('dashboard.outlets.statusArchived'),
      };
    case 'provisioned':
    default:
      return { bg: 'var(--color-border)', fg: 'var(--color-on-surface-muted)', label: status };
  }
}

function statusLabel(t: I18n, status: string): string {
  switch (status) {
    case 'trial':
      return t.t('dashboard.outlets.planStatusTrial');
    case 'active':
      return t.t('dashboard.outlets.planStatusActive');
    case 'past_due':
      return t.t('dashboard.outlets.planStatusPastDue');
    case 'suspended':
      return t.t('dashboard.outlets.planStatusSuspended');
    case 'archived':
      return t.t('dashboard.outlets.planStatusArchived');
    default:
      return status;
  }
}

function extractFriendlyError(t: I18n, err: unknown): string {
  if (err && typeof err === 'object' && 'message' in err) {
    return String((err as { message: string }).message);
  }
  return t.t('dashboard.outlets.errAddOutletFailed');
}

function Screen({ body }: { readonly body: string }) {
  return (
    <main className="min-h-dvh flex items-center justify-center p-8 text-center">
      <p style={{ fontSize: 'var(--text-meta)', color: 'var(--color-on-surface-muted)' }}>{body}</p>
    </main>
  );
}

// ── styles ──────────────────────────────────────────────────────────────

const fieldLabel: React.CSSProperties = {
  fontSize: 'var(--text-meta)',
  color: 'var(--color-on-surface-muted)',
  fontWeight: 500,
};

const fieldGrid: React.CSSProperties = { display: 'grid', gap: 'var(--space-2)' };

const miniGhostButtonStyle: React.CSSProperties = {
  background: 'transparent',
  color: 'var(--color-on-surface-muted)',
  padding: '6px 10px',
  border: '1px solid var(--color-border-strong)',
  borderRadius: 'var(--radius-sm)',
  fontSize: 'var(--text-meta)',
  cursor: 'pointer',
};

function indexPendingRemovals(events: readonly SubscriptionEvent[]): Map<string, PendingRemoval> {
  // Walk events oldest-first. Track each outlet's most-recent scheduled
  // removal; clear it when a matching cancellation comes through.
  const sorted = events.slice().sort((a, b) => tsMs(a.at) - tsMs(b.at));
  const map = new Map<string, PendingRemoval>();
  for (const ev of sorted) {
    if (ev.kind === 'outlet_removal_scheduled') {
      map.set(ev.outletId, {
        eventId: ev.id,
        effectiveAtMs: tsMs(ev.effectiveAt),
      });
    } else if (ev.kind === 'outlet_removal_cancelled') {
      const current = map.get(ev.outletId);
      if (current && current.eventId === ev.cancelledEventId) {
        map.delete(ev.outletId);
      }
    }
  }
  return map;
}

function tsMs(ts: { toMillis?: () => number; seconds: number }): number {
  return typeof ts.toMillis === 'function' ? ts.toMillis() : ts.seconds * 1000;
}

function formatDate(ms: number): string {
  if (!ms) return '—';
  return new Date(ms).toLocaleDateString();
}

function endOfMonthLocalDate(): number {
  const now = new Date();
  const last = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999);
  return last.getTime();
}
